import * as Yup from 'yup';

import { Address } from '../../../../../types/address';
import {
  yupMaxField,
  yupMinField,
  yupRequiredField,
} from '../../../../../utils/helpers/yup';
import { LogisticsRoute } from '../../LogisticsRoutes.functions';

export type Coordinates = [number, number];

export type RoutePoints = {
  entityId: number | undefined;
  pointName?: string;
  pointTypeId?: number;
  sequence: number;
  maintenanceTimeframe: string;
  address?: Address | null;
};

export type CreateLogisticsPoint = {
  name: string;
  employees?: { value: number; label: string }[]; // for FE logic
  transporters?: number[] | null; // for BE prop, define on toApiData function
  points: RoutePoints[];
};

export type LogisticsPointEntities = {
  id: number;
  name: string;
  address: Address;
};

export function setNewFieldsIndexes(fields: RoutePoints[]) {
  for (let i = 0; i < fields.length; i++) {
    fields[i].sequence = i + 1;
  }
}

export function getInitialValues(
  isEditDialog: boolean,
  routeData: LogisticsRoute | undefined
): CreateLogisticsPoint {
  return {
    name: isEditDialog ? routeData?.name ?? '' : '',
    transporters: isEditDialog
      ? routeData?.transporters?.map((item) => item.id)
      : [],
    employees: isEditDialog
      ? routeData?.transporters?.map((item) => ({
          value: item.id,
          label: item.name,
        }))
      : [],
    points: isEditDialog
      ? routeData?.points
          ?.sort((a, b) => a.sequence - b.sequence)
          ?.map((item) => ({
            entityId: item.entityId,
            pointName: item.pointName,
            pointTypeId: item.pointTypeId,
            sequence: item.sequence,
            maintenanceTimeframe: item.maintenanceTimeframe,
            address: item.address,
          })) ?? [
          {
            entityId: 1,
            pointName: '',
            pointTypeId: 1,
            sequence: 1,
            maintenanceTimeframe: '09:00-16:00',
            address: null,
          },
        ]
      : [
          {
            entityId: 1,
            pointName: '',
            pointTypeId: 1,
            sequence: 1,
            maintenanceTimeframe: '09:00-16:00',
            address: null,
          },
        ],
  };
}

export function getValidationSchema(t: any) {
  return Yup.object().shape({
    name: Yup.string()
      .required(yupRequiredField(t, t('Name')))
      .min(2, ({ min }) => yupMinField(t, t('Name'), min))
      .max(50, ({ max }) => yupMaxField(t, t('Name'), max)),
    employees: Yup.array()
      .nullable()
      .required(yupRequiredField(t, t('Employees'))),
    points: Yup.array()
      .of(
        Yup.object({
          entityId: Yup.number().required(yupRequiredField(t, t('Entity ID'))),
          pointName: Yup.string().optional(),
          pointTypeId: Yup.number().required(
            yupRequiredField(t, t('Point type ID'))
          ),
          sequence: Yup.number().required(yupRequiredField(t, t('Sequence'))),
          maintenanceTimeframe: Yup.string().required(
            yupRequiredField(t, t('Maintenance Timeframe'))
          ),
        })
      )
      .test({
        message: yupRequiredField(t, t('Points')),
        test: (arr) => arr?.length !== 0,
      }),
    transporters: Yup.array().optional(), // this prop is handle in toApiData function for BE
  });
}

export function toApiData(values: CreateLogisticsPoint) {
  let apiData: CreateLogisticsPoint = {
    ...values,
  };

  apiData.name = values.name.trim();

  apiData.points = values.points.map((item: RoutePoints, index) => ({
    entityId: item.entityId,
    pointTypeId: item.pointTypeId,
    sequence: index + 1,
    maintenanceTimeframe: item.maintenanceTimeframe,
  }));

  apiData.transporters =
    values.employees && values.employees?.length > 0
      ? values.employees.map((item: any) => item.value)
      : null;

  if (!apiData.transporters) {
    delete apiData.transporters;
  }
  delete apiData.employees;

  return apiData;
}

export function newPointsIndexes(fields: any) {
  for (let i = 0; i < fields.length; i++) {
    fields[i].index = i + 1;
  }
}
